import { emptySplitApi } from "./emptyApi"

/**
 * Audits — the append-only action history, split from the jobs system. Jobs
 * execute background work; audits record what was done. A bulk action (e.g. a
 * Budget Recommender approve over N entities) is ONE audit whose `items`
 * carry every affected row with its previous → new values and outcome.
 */

/**
 * `items` row of a `budget-recommendation` audit. Budgets are in cents.
 * Mirrors the backend `BudgetRecommendationAuditItem`.
 */
export interface BudgetRecommendationAuditItem {
  /** API platform slug ('meta' | 'tiktok'). */
  platform: string
  entity: "campaign" | "adset"
  entityId: string
  entityName?: string
  /** Parent campaign external id (equals entityId for campaign rows). */
  campaignId?: string
  /** Initiative type the row was classified as when the recommendation was made. */
  initiativeType?: string
  /** Display channel ("Meta", "TikTok"). */
  channel?: string
  /** Effective daily budget in cents before this change. Unit in the name (MD-5572). */
  previousDailyBudgetCents?: number
  /** New daily budget in cents — absent when action is 'pause' or 'skip'. Unit in the name (MD-5572). */
  newDailyBudgetCents?: number
  /** @deprecated Rows written before 2026-09-29 carry the cents values under these unlabeled names; readers fall back to them. */
  previousDailyBudget?: number
  /** @deprecated See previousDailyBudget. */
  newDailyBudget?: number
  action: "update" | "pause" | "skip"
  success: boolean
  error?: string
  /**
   * action 'skip': why the row was left alone. 'no-initiative-type': an
   * active row with no type, kept at its current budget in a batch the user
   * approved past the initiative-type gate. 'manual-change-hold' only
   * appears on rows written before 2026-10-20, when a recent manual change
   * pinned the row; it is a label now (see overrodeRecentChange).
   */
  skipReason?: "manual-change-hold" | "not-selected" | "no-initiative-type"
  /** @deprecated The hold that caused a pre-2026-10-20 skip. */
  holdReason?: "budget" | "bid-target" | "enabled"
  /** @deprecated See holdReason. */
  holdAuditId?: string
  /** @deprecated See holdReason. */
  holdAt?: string
  /** @deprecated See holdReason. */
  holdActor?: string | null
  /**
   * The row carried a recent-change label (a human/agent budget, bid target
   * or enable change inside the recent change window, or its first spend
   * day) and the user acknowledged it before approving.
   */
  overrodeRecentChange?: true
  recentChangeReason?: "budget" | "bid-target" | "enabled" | "first-spend"
  /** ISO timestamp of the audited change, or the YYYY-MM-DD first spend day. */
  recentChangeAt?: string
  /** The Audit row that proves an audited change; null for first spend. */
  recentChangeAuditId?: string | null
  recentChangeActor?: string | null
  /** Every label on the row, as shown to the user. */
  recentChangeNote?: string
  /**
   * An active, typed row some of whose spending ad sets have no initiative
   * type: the initiative-type gate flagged it and the user approved past
   * the gate.
   */
  untypedAdsets?: true
  /** Combined smart-scale rows: the tROAS moved alongside the budget. */
  previousTargetRoas?: number
  newTargetRoas?: number
  /** Where the tROAS suggestion came from. */
  troasSource?: "simulation" | "heuristic"
  troasApplied?: boolean
  troasError?: string

  // ── Recommendation context ────────────────────────────────────────
  // Why the recommender said what it said, frozen at approve time, so an
  // external grader re-scoring the call shares one vocabulary with the app
  // instead of reconstructing the window and metrics. Per-ROW, not per-batch:
  // the analysis window is anchored per row (Google Demand Gen lags).

  /**
   * Confidence tier from estimated conversions; null when AOV is unset.
   * Union inlined rather than importing `ConfidenceTier` — the api layer does
   * not depend on page modules.
   */
  confidence?: "LOW" | "MEDIUM" | "HIGH" | null
  /** Length of this row's analysis window in days (3 / 7 / 14 / 30). */
  analysisPeriodDays?: number
  /** Inclusive end date (YYYY-MM-DD) of this row's analysis window. */
  dataThroughDate?: string
  /** Direct-response ROAS over the window (modeled revenue / spend). */
  drRoas?: number
  /** Brand ROAS over the window (brand value / spend). */
  brandRoas?: number
}

/** Snapshot of the recommender inputs a budget-recommendation batch was generated with. */
export interface BudgetRecommendationAuditContext {
  /** Target total daily budget in dollars. */
  targetDailyBudget?: number
  targetDRRoas?: number
  targetBrandRoas?: number
}

export interface CreateAuditArgs {
  /** Action type (kebab-case slug), e.g. "budget-recommendation". */
  type: string
  /** Snapshot of the inputs the action was taken with. */
  context?: Record<string, unknown>
  /** Per-entity rows; `success: false` marks a failed row. */
  items: unknown[]
}

/** Mirrors the backend AuditDto (one `Audit` table row). */
export interface AuditView {
  id: string
  companyId: string
  type: string
  /** JobRun that executed the action (framework-written audits only). */
  jobRunId: string | null
  context: Record<string, unknown> | null
  items: unknown[]
  total: number
  successful: number
  failed: number
  userId: string | null
  createdAt: string
}

export interface ListAuditsArgs {
  type?: string
  /** Only audits created at/after this ISO datetime. */
  from?: string
  /** Only audits created at/before this ISO datetime. */
  to?: string
  take?: number
  skip?: number
}

export const auditsApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Record an action in the audit history. Append-only; one call per user
     * action — bulk actions send every affected row in `items`.
     */
    createAudit: builder.mutation<AuditView, CreateAuditArgs>({
      query: body => ({ url: "audits", method: "POST", body }),
    }),
    listAudits: builder.query<AuditView[], ListAuditsArgs | void>({
      query: arg => {
        const args: ListAuditsArgs = arg || {}
        const params: Record<string, string> = {}
        if (args.type) params.type = args.type
        if (args.from) params.from = args.from
        if (args.to) params.to = args.to
        if (args.take !== undefined) params.take = String(args.take)
        if (args.skip !== undefined) params.skip = String(args.skip)
        return {
          url: "audits",
          params: Object.keys(params).length ? params : undefined,
        }
      },
    }),
  }),
})

export const { useCreateAuditMutation, useListAuditsQuery } = auditsApi
